import { z } from 'zod'

import { blockOf, mediaIdSchema, mediaSchema } from './common'
import { BlockType } from './types'

export const imageBlockSchema = z.object({
    ...blockOf(BlockType.IMAGE),
    mediaId: mediaIdSchema,
    media: mediaSchema,
    mediaMobileId: mediaIdSchema,
    mediaMobile: mediaSchema,
    /** Source or credit line shown under the image. */
    caption: z.string(),
})

export type ImageBlockData = z.infer<typeof imageBlockSchema>

/**
 * How much of the page the video takes.
 *
 * `FULL` runs edge to edge, which suits a page that opens on one. `CONTAINED` keeps it inside the
 * same column as the surrounding text, which is what a video illustrating a paragraph wants.
 */
export const VideoWidth = {
    FULL: 'FULL',
    CONTAINED: 'CONTAINED',
} as const

export type VideoWidth = (typeof VideoWidth)[keyof typeof VideoWidth]

export const videoWidthSchema = z.enum(VideoWidth)

export const fullWidthVideoBlockSchema = z.object({
    ...blockOf(BlockType.FULL_WIDTH_VIDEO),
    /**
     * A youtube url rather than an uploaded file. Editorial video is measured in hundreds of
     * megabytes and wants a player, adaptive bitrates and a cdn — none of which an asset library
     * should be in the business of providing.
     */
    youtubeUrl: z.string(),
    /** Defaults to full width, so blocks stored before this field existed keep their layout. */
    width: videoWidthSchema.default(VideoWidth.FULL),
    /** Shown before the embed loads, and in place of it when no url is set. */
    imageMediaId: mediaIdSchema,
    imageMedia: mediaSchema,
    imageMediaMobileId: mediaIdSchema,
    imageMediaMobile: mediaSchema,
})

export type FullWidthVideoBlockData = z.infer<typeof fullWidthVideoBlockSchema>
